import { ITwoFAGeneratePurpose } from '~/@api/generated-api-types-user/Api'
import { Dialog, DialogContent } from '~/shadcn/ui/dialog'
import { HeadingTwo } from '~/@mid_ui/typography/HeadingTwo'
import { useLocalNotifications } from '~/@mid_ui/local-notifications/useLocalNotifications'
import { TwoFaVerifyForm } from '~/@mid_2fa/twofa-verify-form/TwoFaVerifyForm'
import { useTwoFa } from '~/@mid_2fa/twofa/useTwoFa'
import { useUser } from '~/@biz_account/useUser/useUser'
import {
  AddBankAccountForm,
  type AddBankAccountFormData,
} from './AddBankAccountForm'
import { useAddBankAccount } from './useAddBankAccount'
import { useTwoFaCodeExpirationCountdown } from '~/@mid_2fa/twofa/useTwoFaCodeExpirationCountdown'
import { Text } from '~/@mid_ui/typography/Text'
import { Spinner } from '~/shadcn/ui/spinner'
import { Button } from '~/shadcn/ui/button/button'
import { CodeExpirationCountdown } from '~/@mid_2fa/twofa/CodeExpirationCountdown'
import { HeadingThree } from '~/@mid_ui/typography/HeadingThree'

type Props = {
  onSuccess: () => void
  onClose: () => void
}

function AddBankAccountDialog({ onSuccess, onClose }: Props) {
  const { setNotification } = useLocalNotifications()
  const { user } = useUser()
  const verifiedPhoneNumber = user?.profile?.phone

  const {
    generateTwoFaCode: generateTwoFaCodeMutation,
    isGeneratingTwoFaCode,
    twoFaCodeExpiresAt,
    error: twoFaError,
  } = useTwoFa()

  const { countdown, hasCodeExpired } =
    useTwoFaCodeExpirationCountdown(twoFaCodeExpiresAt)

  const {
    addStep,
    setAddStep,
    addBankAccount: addBankAccountMutation,
    isAddingBankAccount,
    addBankAccountPayload,
    setAddBankAccountPayload,
    error: addBankAccountError,
  } = useAddBankAccount()

  function handleGenerateTwoFaCode(payload: AddBankAccountFormData) {
    generateTwoFaCodeMutation(
      {
        purpose: ITwoFAGeneratePurpose.AddBankAccount,
        data: { iban: payload.iban },
      },
      {
        onSuccess: ({ reused }) => {
          setAddStep('verify')
          if (!reused)
            setNotification('success', 'SMS kód bol úspešne generovaný.')
        },
      }
    )
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="px-6 md:px-12 py-12 bg-background">
        {addStep === 'add' && (
          <>
            <div className="flex flex-col items-start gap-2">
              <HeadingTwo className="text-2xl max-w-md mb-2">
                Pridajte si ďalší bankový účet
              </HeadingTwo>
              <p className="text-md font-semibold max-w-md">
                Preveďte prostriedky na svoj účet jednoduchým bankovým prevodom.
              </p>
              <p className="text-md font-semibold max-w-md">
                Po vykonaní prevodu z vášho bankového účtu na účet Invest in
                Slovakia podľa variabilného symbolu spárujeme platbu s Vaším
                investorským účtom.
              </p>
            </div>
            <AddBankAccountForm
              error={twoFaError}
              onSubmit={(formData: AddBankAccountFormData) => {
                setAddBankAccountPayload({
                  ...addBankAccountPayload,
                  ...formData,
                })
                handleGenerateTwoFaCode(formData)
              }}
              isRequesting={isGeneratingTwoFaCode}
            />
          </>
        )}

        {addStep === 'verify' && (
          <>
            {hasCodeExpired && (
              <div className="flex flex-col items-center gap-4 text-center">
                <Text>SMS kód vypršal.</Text>
                <Button
                  variant="outline"
                  onClick={() =>
                    handleGenerateTwoFaCode(addBankAccountPayload)
                  }>
                  Znova odoslať SMS kód{` `}
                  {isGeneratingTwoFaCode && <Spinner className="size-4" />}
                </Button>
              </div>
            )}
            {!hasCodeExpired && (
              <TwoFaVerifyForm
                isRequesting={isAddingBankAccount}
                error={addBankAccountError}
                handleSubmitForm={({ code }) => {
                  addBankAccountMutation(
                    {
                      ...addBankAccountPayload,
                      twoFACode: code,
                    },
                    {
                      onSuccess,
                    }
                  )
                }}
                formContent={
                  <div className="flex flex-col items-center gap-6">
                    <HeadingThree className="text-s max-w-md text-center">
                      V rámci ochrany investora potvrďte, že tento bankový účet
                      je váš
                    </HeadingThree>
                    <p className="text-md max-w-md text-center">
                      Overte svoj účet zadaním SMS kódu,
                      <br />
                      ktorý vám bol zaslaný na číslo {verifiedPhoneNumber}.
                      <br />
                      <CodeExpirationCountdown countdown={countdown} />
                    </p>
                  </div>
                }
                fieldLabel="Overovací SMS kód"
                submitButtonLabel="Potvrdiť"
                FooterComponent={(props) => (
                  <div
                    className="flex flex-col items-center justify-center mt-8 gap-2 w-full"
                    {...props}
                  />
                )}
              />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export { AddBankAccountDialog }
